import { useEffect, useState } from "react";
import { MapPin, Briefcase, ArrowRight, Inbox } from "lucide-react";
import api, { COMPANY } from "../../lib/api";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api
      .get("/jobs")
      .then((res) => setJobs(res.data))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="careers"
      data-testid="careers-section"
      className="relative py-24 sm:py-32 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-6 items-end mb-14">
          <div className="col-span-12 lg:col-span-7">
            <div className="overline mb-4">03 / Καριέρα</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white">
              Ελάτε στην
              <span className="text-[#E63946]"> ομάδα</span> μας.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[#A1A1AA] text-base sm:text-lg leading-relaxed">
              Δείτε τις ανοιχτές θέσεις εργασίας. Ψάχνουμε πάντα για
              τεχνικούς που μοιράζονται το πάθος μας για άρτια κατασκευή και
              ταχύτητα στην αποκατάσταση.
            </p>
          </div>
        </div>

        {loading ? (
          <JobsSkeleton />
        ) : jobs.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="border border-[#27272A] divide-y divide-[#27272A]">
            {jobs.map((j) => (
              <button
                key={j.id}
                onClick={() => setSelected(j)}
                data-testid={`job-row-${j.id}`}
                className="w-full text-left px-5 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-[#0F0F11] transition-colors group"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono-th text-[10px] tracking-[0.25em] text-[#E63946] uppercase">
                      {j.employment_type}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl sm:text-2xl text-white font-medium">
                    {j.title}
                  </h3>
                  <div className="mt-2 flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <MapPin size={14} />
                    {j.location}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[#E63946] text-sm font-medium">
                  Λεπτομέρειες
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <JobDialog job={selected} onClose={() => setSelected(null)} />
      )}
    </section>
  );
}

function JobsSkeleton() {
  return (
    <div className="border border-[#27272A] divide-y divide-[#27272A]">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="px-5 sm:px-8 py-6 flex justify-between items-center"
        >
          <div className="space-y-2 flex-1">
            <div className="h-3 w-24 bg-[#16161A]" />
            <div className="h-6 w-2/3 bg-[#16161A]" />
            <div className="h-3 w-32 bg-[#16161A]" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="careers-empty"
      className="border border-[#27272A] py-20 px-6 text-center"
    >
      <Inbox size={32} className="text-[#E63946] mx-auto mb-4" strokeWidth={1.5} />
      <h3 className="font-heading text-xl text-white mb-2 font-medium">
        Δεν υπάρχουν διαθέσιμες θέσεις αυτή τη στιγμή
      </h3>
      <p className="text-[#A1A1AA] text-sm max-w-md mx-auto">
        Στείλτε μας ένα βιογραφικό στο{" "}
        <a
          href={`mailto:${COMPANY.email}`}
          className="text-[#E63946] hover:underline"
        >
          {COMPANY.email}
        </a>{" "}
        για να σας λάβουμε υπόψη σε μελλοντικές ευκαιρίες.
      </p>
    </div>
  );
}

function JobDialog({ job, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      data-testid="job-dialog"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm fade-up"
      onClick={onClose}
    >
      <div
        className="relative bg-[#0F0F11] border border-[#27272A] max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 sm:px-10 py-8 border-b border-[#27272A]">
          <div className="flex items-center gap-3 mb-3">
            <Briefcase size={16} className="text-[#E63946]" />
            <span className="font-mono-th text-[10px] tracking-[0.25em] text-[#E63946] uppercase">
              {job.employment_type}
            </span>
          </div>
          <h3 className="font-heading text-2xl sm:text-3xl text-white font-medium tracking-tight">
            {job.title}
          </h3>
          <div className="mt-2 flex items-center gap-2 text-sm text-[#A1A1AA]">
            <MapPin size={14} />
            {job.location}
          </div>
        </div>

        <div className="px-6 sm:px-10 py-8 space-y-6">
          <div>
            <div className="overline mb-3">Περιγραφή Θέσης</div>
            <p className="text-[#FAFAFA]/90 leading-relaxed whitespace-pre-wrap">
              {job.description}
            </p>
          </div>
          {job.requirements && (
            <div>
              <div className="overline mb-3">Απαιτήσεις</div>
              <p className="text-[#FAFAFA]/90 leading-relaxed whitespace-pre-wrap">
                {job.requirements}
              </p>
            </div>
          )}
        </div>

        <div className="px-6 sm:px-10 py-6 border-t border-[#27272A] flex flex-wrap items-center gap-3 justify-between">
          <button
            onClick={onClose}
            data-testid="job-dialog-close-btn"
            className="text-sm text-[#A1A1AA] hover:text-white px-4 py-2"
          >
            Κλείσιμο
          </button>
          <a
            href={`mailto:${COMPANY.email}?subject=${encodeURIComponent(`Αίτηση για: ${job.title}`)}`}
            data-testid="job-apply-email-btn"
            className="btn-primary px-6 py-3 text-sm font-medium inline-flex items-center gap-2"
          >
            Στείλε CV στο {COMPANY.email}
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </div>
  );
}
